import getComponentInstanceFor from '@swey/vue-hydration/getComponentInstanceFor';
import VideoComponent from '@components/008-VideoComponent/VideoComponent.client';
import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';

export default class Accordion {
	private readonly element: HTMLElement;

	public constructor(element: HTMLElement) {
		this.element = element;
		this.init();
	}

	private init(): void {
		const accordionItems: HTMLElement[] = Array.from(this.element.querySelectorAll('.m-accordion__item'));
		accordionItems.forEach((accordionItem: HTMLElement): void => {
			const toggle: HTMLElement | null = accordionItem.querySelector('.m-accordion__toggle');

			if (!toggle) return;

			toggle.addEventListener('click', (event: MouseEvent): void => {
				event.preventDefault();

				this.clickedAccordion(accordionItem);
			});
		});

		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler.bind(this));
	}

	protected clickedAccordion(accordionItem: HTMLElement): void {
		const accordionItemContent: HTMLElement | null = accordionItem.querySelector('.m-accordion__content');
		const isActive = accordionItem.classList.contains('is-active');

		if (!accordionItemContent) return;

		if (isActive) {
			this.closeItem(accordionItem);
		} else {
			this.openItem(accordionItem);
		}
	}

	protected openItem(accordionItem: HTMLElement): void {
		this.closeActive();
		Accordion.updateItemAttributes(accordionItem, true);
		Accordion.animateItem(accordionItem, true);
		accordionItem.classList.add('is-active');
	}

	protected closeItem(accordionItem: HTMLElement): void {
		Accordion.updateItemAttributes(accordionItem, false);
		Accordion.animateItem(accordionItem, false);
		accordionItem.classList.remove('is-active');

		const videos: HTMLElement[] = Array.from(accordionItem.querySelectorAll('.c-video-component'));

		videos.forEach((videoElement): void => {
			const videoComponent: VideoComponent = getComponentInstanceFor(videoElement) as VideoComponent;
			videoComponent.closeVideo();
		});
	}

	private anchorLinkHandler(event: CustomEventInit): void {
		const targetElement = this.element.querySelector(event.detail.target);

		if (targetElement) {
			const contentPane = targetElement.closest('.m-accordion__item');

			if (contentPane) {
				this.openItem(contentPane);
			}
		}
	}

	protected static animateItem(accordionItem: HTMLElement, isActive: boolean): void {
		const accordionItemContent: HTMLElement | null = accordionItem.querySelector('.m-accordion__content');

		if (!accordionItemContent) return;

		// Set explicit height to current visible height (for transition)
		accordionItemContent.style.height = `${accordionItemContent.clientHeight}px`;

		// Start transition with a small delay
		window.setTimeout((): void => {
			accordionItemContent.style.height = isActive ? `${accordionItemContent.scrollHeight}px` : null;
		}, 10);

		// Remove explicit height again after transition to avoid problems after resizing window
		window.setTimeout((): void => {
			accordionItemContent.style.height = null;
		}, 310);
	}

	protected closeActive(): void {
		const activeAccordionItem: HTMLElement | null = this.element.querySelector('.m-accordion__item.is-active');

		if (!activeAccordionItem) return;

		this.closeItem(activeAccordionItem);
	}

	protected static updateItemAttributes(accordionItem: HTMLElement, isActive: boolean): void {
		const accordionButton: HTMLElement | null = accordionItem.querySelector('.m-accordion__item-button');
		const accordionContent: HTMLElement | null = accordionItem.querySelector('.m-accordion__content');

		if (accordionButton) {
			accordionButton.setAttribute('aria-expanded', isActive ? 'true' : 'false');
		}

		if (accordionContent) {
			accordionContent.setAttribute('aria-hidden', !isActive ? 'true' : 'false');
		}
	}
}
